import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Renderer2,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { BadgeModule } from 'primeng/badge';

import { TranslocoModule } from '@jsverse/transloco';

import { ButtonComponent } from '../button/button.component';
import { SidebarConfig } from './sidebar.interface';

const FLOATING_BELOW_PX = 1042;

@Component({
  selector: 'nbx-sidebar',
  standalone: true,
  imports: [BadgeModule, ButtonComponent, RouterLink, RouterLinkActive, TranslocoModule],
  templateUrl: './sidebar.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnDestroy {
  private readonly _renderer = inject(Renderer2);
  private _resizeListener?: () => void;

  public readonly config = input.required<SidebarConfig>();
  public readonly styleClass = input<string>('');

  public collapsed = model<boolean>(false);

  public readonly showCloseButton = computed(() => {
    const overlay = this.isTablet() || this.isFloating();
    const isCollapsed = this.collapsed();
    return overlay && !isCollapsed;
  });

  public readonly isTablet = signal(typeof window !== 'undefined' && window.innerWidth < 745);

  public readonly isFloating = signal(
    typeof window !== 'undefined' && window.innerWidth < FLOATING_BELOW_PX
  );

  constructor() {
    if (typeof window !== 'undefined') {
      // Only track the breakpoint here. Collapsing belongs to the effect below, which fires when the
      // viewport actually crosses into tablet; collapsing on every resize event shut an expanded
      // sidebar on any drag of the window edge, at any width.
      this._resizeListener = this._renderer.listen('window', 'resize', () => {
        this.isTablet.set(window.innerWidth < 745);
        this.isFloating.set(window.innerWidth < FLOATING_BELOW_PX);
      });
    }

    effect(() => {
      const overlay = this.isTablet() || this.isFloating();

      if (overlay) {
        this.collapsed.set(true);
      }
    });
  }

  /** Following a link from an overlaid sidebar puts it away, so the page it opens is not covered. */
  onItemClick(): void {
    if (this.isTablet() || this.isFloating()) {
      this.collapsed.set(true);
    }
  }

  ngOnDestroy(): void {
    if (this._resizeListener) {
      this._resizeListener();
    }
  }
}
